import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { C, font, text, scopeLabel } from "./theme";
import { api } from "./api";
import { Notice } from "./components/ui";
import { takeFlash } from "./lib/flash";
import Dashboard from "./pages/Dashboard";
import AORPage from "./pages/AORPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import POPage from "./pages/POPage";
import TenderRequestPage from "./pages/TenderRequestPage";
import VendorEvaluationPage from "./pages/VendorEvaluationPage";
import TenderEvaluationsPage from "./pages/TenderEvaluationsPage";
import GRNPage from "./pages/GRNPage";
import InvoicePage from "./pages/InvoicePage";
import PaymentsPage from "./pages/PaymentsPage";
import BudgetPage from "./pages/BudgetPage";
import VirementPage from "./pages/VirementPage";
import SettingsPage from "./pages/SettingsPage";
import AuditPage from "./pages/AuditPage";
import ReportsPage from "./pages/ReportsPage";
import { usePermissions } from "./PermissionContext";
import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import TwoFactorEnroll from "./pages/TwoFactorEnroll";

// Admin pages are code-split: most users never open them, so they ship as a
// separate chunk fetched on first visit (health-sweep step 5).
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminRolesPage = lazy(() => import("./pages/AdminRolesPage"));
const AdminWorkflowsPage = lazy(() => import("./pages/AdminWorkflowsPage"));
const AdminReferencePage = lazy(() => import("./pages/AdminReferencePage"));
const AdminHealthPage = lazy(() => import("./pages/AdminHealthPage"));
const AdminActivityPage = lazy(() => import("./pages/AdminActivityPage"));

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: "📊", module: null },
  { id: "virement", label: "Virements", icon: "🔀", module: "virement" },
  { id: "budget", label: "Budget", icon: "💰", module: "budget" },
  { id: "aor", label: "Approval of Needs Requests", icon: "📋", module: "aor" },
  { id: "po", label: "Purchase Orders", icon: "🚚", module: "po" },
  { id: "tender-requests", label: "Tender Requests", icon: "📑", module: "tenderRequest" },
  { id: "vendor-eval", label: "Vendor Evaluation", icon: "🏷️", module: "vendorEvaluation" },
  { id: "tender-evals", label: "Pending Evaluations", icon: "📝", module: "vendorEvaluation" },
  { id: "grn", label: "Goods Receipt", icon: "📦", module: "grn" },
  // Approvals carries the AOR-chain, payment-batch and virement queues; the
  // server's /api/approvals endpoints accept any of these verbs, so the nav
  // must too — the Payment Approver preset holds payment:view but no aor
  // permission, and Finance holds virement:approve with no aor permission (UX-45).
  { id: "approvals", label: "Approvals", icon: "✅", modules: ["aor", "payment", "virement"] },
  { id: "invoice", label: "Invoices", icon: "🧾", module: "invoice" },
  { id: "payments", label: "Payment Runs", icon: "💸", module: "payment" },
  { id: "reports", label: "Reports", icon: "🔍", module: "reports" },
  { id: "audit", label: "Audit Trail", icon: "📋", module: "audit" },
  { id: "settings", label: "Settings", icon: "⚙️", module: "settings" },
];

// A render error on one page must not blank the whole app (there is no URL
// routing — a reload loses the user's place). Keyed by page, so navigating
// elsewhere mounts a fresh boundary and recovers.
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div role="alert" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: text.h2, fontWeight: 700, color: C.gray1100, marginBottom: 8 }}>This page hit an unexpected error</div>
          <div style={{ fontSize: text.md, color: C.gray600, marginBottom: 16 }}>The rest of the app still works — use the sidebar, or try this page again.</div>
          <button onClick={() => this.setState({ error: null })} style={{ background: C.primary, color: C.white, border: "none", borderRadius: 6, padding: "8px 16px", fontSize: text.sm, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ADMIN_NAV = [
  { id: "admin-users", label: "Users", icon: "👤", module: "users" },
  { id: "admin-roles", label: "Roles & Permissions", icon: "🔐", module: "roles" },
  { id: "admin-workflows", label: "Approval Workflows", icon: "🔀", module: "workflows" },
  { id: "admin-reference", label: "Reference Data", icon: "🗂️", module: "reference" },
  { id: "admin-health", label: "System Health", icon: "💚", module: "users" },
  { id: "admin-activity", label: "Admin Activity", icon: "📜", module: "audit" },
];

// Hash-based routing (UX-16): the current view lives in location.hash as
// #/<page> or #/<page>/<docId>, so reloads keep the user's place, documents
// are deep-linkable/bookmarkable, and back/forward work.
const ALL_PAGE_IDS = new Set([...NAV, ...ADMIN_NAV].map((n) => n.id));
const parseHash = () => {
  const m = /^#\/([\w-]+)(?:\/([\w-]+))?/.exec(window.location.hash || "");
  if (!m || !ALL_PAGE_IDS.has(m[1])) return null;
  return { page: m[1], docId: m[2] || null };
};
const writeHash = (pageId, docId) => {
  const next = "#/" + pageId + (docId ? "/" + docId : "");
  if (window.location.hash !== next) window.location.hash = next;
};

export default function App() {
  const initial = parseHash();
  const [page, setPage] = useState(initial ? initial.page : "dashboard");
  const [pendingCount, setPendingCount] = useState(0);
  const [evalCount, setEvalCount] = useState(0);
  // Cross-page document link: { page, id } set by openDoc (or a deep link),
  // cleared on manual navigation. The target page opens that document on mount.
  const [docTarget, setDocTarget] = useState(initial && initial.docId ? { page: initial.page, id: initial.docId } : null);
  // One-shot confirmation carried across an auth-gate transition (UX-81).
  const [flash, setFlash] = useState(() => takeFlash());

  const { me, can, reload, authed, loading, sessionExpired } = usePermissions();

  // The 2FA-enrollment gate completes without a full page load: pick up its
  // "Two-factor authentication enabled." flash when the shell appears.
  useEffect(() => {
    if (authed && me && !me.mustChangePassword && !me.twoFactorPending) {
      const f = takeFlash();
      if (f) setFlash(f);
    }
  }, [authed, me]);

  const logout = async () => { await fetch("/api/auth/logout", { method: "POST" }); window.location.hash = ""; window.location.reload(); };

  // The gate flash is a one-shot acknowledgment: navigating away dismisses it.
  const navigate = (id) => { setFlash(null); setDocTarget(null); setPage(id); writeHash(id); };
  const openDoc = (pageId, docId) => { setFlash(null); setDocTarget({ page: pageId, id: docId }); setPage(pageId); writeHash(pageId, docId); };

  // Back/forward and hand-edited deep links drive the same state.
  useEffect(() => {
    const onHash = () => {
      const h = parseHash();
      if (!h) return;
      setDocTarget(h.docId ? { page: h.page, id: h.docId } : null);
      setPage(h.page);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Focus + title management (UX-74): every navigation retitles the document
  // and moves focus to the content region, so keyboard users don't re-Tab from
  // the top and screen readers hear where they landed. Auth pages set their own
  // titles, so this runs only once the signed-in shell is showing.
  const mainRef = useRef(null);
  const prevPageRef = useRef(null);
  const inShell = authed && me && !me.mustChangePassword && !me.twoFactorPending;
  useEffect(() => {
    if (!inShell) { prevPageRef.current = null; return; }
    const navItem = [...NAV, ...ADMIN_NAV].find((n) => n.id === page);
    document.title = (navItem ? navItem.label : "Dashboard") + " — SGNIC P2P";
    if (prevPageRef.current !== null && prevPageRef.current !== page && mainRef.current) {
      mainRef.current.focus();
    }
    prevPageRef.current = page;
  }, [page, inShell]);

  // Sidebar badges must track reality, not login time (UX-18): refresh on
  // navigation, whenever a page reports it changed the workqueue (the
  // "workqueue-changed" window event), and on a slow poll for actions taken
  // by other users.
  useEffect(() => {
    const refresh = () => {
      api.getPending().then((p) => setPendingCount(p.length)).catch(() => {});
      if (can("vendorEvaluation", "evaluate")) {
        api.getPendingEvaluations().then((p) => setEvalCount(p.length)).catch(() => setEvalCount(0));
      }
    };
    refresh();
    window.addEventListener("workqueue-changed", refresh);
    const timer = setInterval(refresh, 60000);
    return () => { window.removeEventListener("workqueue-changed", refresh); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (loading) return null;
  if (!authed) return <main><LoginPage signedOut={sessionExpired} onLoggedIn={() => reload()} /></main>;
  if (me && me.mustChangePassword) return <main><ChangePasswordPage onChanged={() => reload()} /></main>;
  if (me && me.twoFactorPending) return <main><TwoFactorEnroll onEnrolled={() => reload()} /></main>;

  // A nav item is visible when the user can view any of its modules
  // (`modules` list, or the single `module`; no module = always visible).
  const canSee = (n) => {
    const mods = n.modules || (n.module ? [n.module] : []);
    return mods.length === 0 || mods.some((m) => can(m, "view"));
  };

  const renderPage = () => {
    const navItem = [...NAV, ...ADMIN_NAV].find((n) => n.id === page);
    if (navItem && !canSee(navItem)) {
      return <Dashboard onNavigate={navigate} onOpenDoc={openDoc} />;
    }
    // The page a doc link targets receives the document id to open on mount.
    const initialId = docTarget && docTarget.page === page ? docTarget.id : null;
    switch (page) {
      case "dashboard": return <Dashboard onNavigate={navigate} onOpenDoc={openDoc} />;
      case "budget": return <BudgetPage onNavigate={navigate} />;
      case "virement": return <VirementPage initialId={initialId} />;
      case "aor": return <AORPage initialId={initialId} onOpenDoc={openDoc} />;
      case "po": return <POPage initialId={initialId} onOpenDoc={openDoc} />;
      case "tender-requests": return <TenderRequestPage initialId={initialId} onOpenDoc={openDoc} />;
      case "vendor-eval": return <VendorEvaluationPage initialId={initialId} onOpenDoc={openDoc} />;
      case "tender-evals": return <TenderEvaluationsPage />;
      case "grn": return <GRNPage />;
      case "invoice": return <InvoicePage initialId={initialId} onOpenDoc={openDoc} />;
      case "payments": return <PaymentsPage initialId={initialId} onOpenDoc={openDoc} onNavigate={navigate} />;
      case "approvals": return <ApprovalsPage onOpenDoc={openDoc} />;
      case "reports": return <ReportsPage />;
      case "audit": return <AuditPage />;
      case "settings": return <SettingsPage />;
      case "admin-users": return <AdminUsersPage />;
      case "admin-roles": return <AdminRolesPage />;
      case "admin-workflows": return <AdminWorkflowsPage />;
      case "admin-reference": return <AdminReferencePage />;
      case "admin-health": return <AdminHealthPage />;
      case "admin-activity": return <AdminActivityPage />;
      default: return <Dashboard onNavigate={navigate} onOpenDoc={openDoc} />;
    }
  };

  return (
    <div style={{ fontFamily: font, background: C.gray50, minHeight: "100vh", display: "flex" }}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      {/* Navy Sidebar — a complementary landmark so the brand block, nav
          labels and connection status all sit inside a region (WCAG 1.3.1). */}
      <aside aria-label="Sidebar" style={{ width: 240, background: C.primary, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <div style={{ padding: "20px 22px 16px" }}>
          <div style={{ color: C.white, fontWeight: 800, fontSize: text.h2, letterSpacing: "0.02em" }}>SGNIC</div>
          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: text.sm, marginTop: 2 }}>Procure-to-Pay</div>
        </div>

        <div style={{ fontSize: text.micro, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, padding: "8px 22px" }}>Modules</div>
        <nav aria-label="Modules">
          {NAV.filter(canSee).map((n) => {
            const active = page === n.id;
            const badge = n.id === "approvals" ? pendingCount : n.id === "tender-evals" ? evalCount : 0;
            return (
              <button key={n.id} onClick={() => navigate(n.id)} aria-current={active ? "page" : undefined} style={{
                position: "relative", width: "100%", border: "none", textAlign: "left", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 10, padding: "9px 22px", cursor: "pointer",
                fontSize: text.md, fontWeight: active ? 700 : 500,
                color: active ? C.white : "rgba(255,255,255,0.75)",
                background: active ? "rgba(255,255,255,0.1)" : "transparent",
                borderLeft: active ? `3px solid ${C.white}` : "3px solid transparent",
              }}>
                <span aria-hidden="true">{n.icon}</span>
                <span style={{ flex: 1 }}>{n.label}</span>
                {badge > 0 && <span style={{ background: C.danger, color: C.white, fontSize: text.micro, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>{badge}</span>}
              </button>
            );
          })}
        </nav>

        {ADMIN_NAV.some((n) => can(n.module, "view")) && (
          <nav aria-label="Admin">
            <div style={{ fontSize: text.micro, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, padding: "12px 22px 4px" }}>Admin</div>
            {ADMIN_NAV.filter((n) => can(n.module, "view")).map((n) => {
              const active = page === n.id;
              return (
                <button key={n.id} onClick={() => navigate(n.id)} aria-current={active ? "page" : undefined} style={{
                  width: "100%", border: "none", textAlign: "left", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 22px", cursor: "pointer",
                  fontSize: text.md, fontWeight: active ? 700 : 500,
                  color: active ? C.white : "rgba(255,255,255,0.75)",
                  background: active ? "rgba(255,255,255,0.1)" : "transparent",
                  borderLeft: active ? `3px solid ${C.white}` : "3px solid transparent",
                }}>
                  <span aria-hidden="true">{n.icon}</span><span style={{ flex: 1 }}>{n.label}</span>
                </button>
              );
            })}
          </nav>
        )}

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", margin: "12px 22px", paddingTop: 12 }}>
          <div style={{ fontSize: text.micro, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Status</div>
          {["API Connected", "PostgreSQL", "Entra ID"].map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: text.sm, color: "rgba(255,255,255,0.7)", padding: "4px 0" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.successLight }} />{s}
            </div>
          ))}
        </div>
      </aside>

      {/* Content column */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", maxHeight: "100vh" }}>
        {/* Slim header (banner landmark) */}
        <header style={{ height: 56, background: C.white, borderBottom: `1px solid ${C.gray200}`, display: "flex", alignItems: "center", padding: "0 24px", gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1 }} />
          <button
            onClick={logout}
            style={{ background: C.white, color: C.gray1100, border: `1px solid ${C.gray200}`, borderRadius: 6, padding: "6px 12px", fontSize: text.sm, fontFamily: "inherit", cursor: "pointer" }}
          >
            Log out
          </button>
          {me && (
            <div style={{ textAlign: "right" }}>
              <div style={{ color: C.gray1100, fontSize: text.sm, fontWeight: 600 }}>{me.displayName}</div>
              <div style={{ color: C.gray500, fontSize: text.micro }} title={`Data scope: ${me.dataScope}`}>{me.roleName} · {scopeLabel(me.dataScope)}</div>
            </div>
          )}
        </header>

        {/* Scrollable content */}
        <main id="main-content" ref={mainRef} tabIndex={-1} style={{ flex: 1, padding: 24, overflowY: "auto", outline: "none" }}>
          {flash && <Notice notice={{ kind: "success", text: flash }} onDismiss={() => setFlash(null)} />}
          <PageErrorBoundary key={page}>
            <Suspense fallback={<div role="status" style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>}>
              {renderPage()}
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
    </div>
  );
}
